import { supabase } from "@/integrations/supabase/client";
import { hasStatisticsConsent, onConsentChange } from "@/lib/consent";
import { isOptedOut } from "@/lib/optOut";

const COOKIE_VID = "dc_vid";
const COOKIE_SID = "dc_sid";
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 min
const FN_BASE = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1`;

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function setCookie(name: string, value: string, days = 365) {
  if (typeof document === "undefined") return;
  const exp = new Date(Date.now() + days * 86400000).toUTCString();
  const secure =
    typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; expires=${exp}; path=/; SameSite=Lax${secure}`;
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Returns the visitor id. Only persists `dc_vid` when the user has granted
 * statistics consent. Without consent, returns a transient id that lives only
 * for this page load (never written to disk).
 */
export function getVisitorId(): string {
  const existing = getCookie(COOKIE_VID);
  if (existing) return existing;
  const fresh = uuid();
  if (hasStatisticsConsent()) {
    setCookie(COOKIE_VID, fresh);
  }
  return fresh;
}

/** Returns current session id, rotating it after 30min idle. Consent-gated. */
export function getSessionId(): string {
  if (typeof sessionStorage === "undefined" || !hasStatisticsConsent()) return uuid();
  const now = Date.now();
  const sid = sessionStorage.getItem(COOKIE_SID);
  const last = parseInt(sessionStorage.getItem(COOKIE_SID + "_t") ?? "0", 10);
  if (sid && now - last < SESSION_IDLE_MS) {
    sessionStorage.setItem(COOKIE_SID + "_t", String(now));
    return sid;
  }
  const fresh = uuid();
  sessionStorage.setItem(COOKIE_SID, fresh);
  sessionStorage.setItem(COOKIE_SID + "_t", String(now));
  return fresh;
}

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const UTM_SNAPSHOT_KEY = "dc_utm_first";

/** Parse utm_* from a query string. Omits empty values. */
function parseUtmFromSearch(search: string): Record<string, string> {
  const sp = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const k of UTM_KEYS) {
    const v = sp.get(k);
    if (v) out[k] = v;
  }
  return out;
}

/** Synchronous first-touch UTM snapshot. Runs at module import — BEFORE the
 *  router mounts and BEFORE any redirect can rewrite the URL — so the landing
 *  UTM survives SPA navigation and the consent-queue wait. First-touch per
 *  session: never overwritten. */
function snapshotLandingUtmOnce(): Record<string, string> | null {
  if (typeof window === "undefined") return null;
  // Read existing snapshot if any (first-touch wins, never overwrite).
  try {
    if (typeof sessionStorage !== "undefined") {
      const existing = sessionStorage.getItem(UTM_SNAPSHOT_KEY);
      if (existing) {
        try {
          return JSON.parse(existing) as Record<string, string>;
        } catch {
          /* fall through and re-snapshot */
        }
      }
    }
  } catch {
    /* sessionStorage may be blocked — fall through to live read */
  }

  const parsed = parseUtmFromSearch(window.location.search);
  if (Object.keys(parsed).length === 0) return null;

  // Best-effort persist; ALWAYS return the parsed object even if the write fails.
  try {
    sessionStorage.setItem(UTM_SNAPSHOT_KEY, JSON.stringify(parsed));
  } catch {
    /* ignore — return parsed below */
  }
  return parsed;
}

// Take the snapshot at module load (top-level side-effect — sync, pre-router).
snapshotLandingUtmOnce();

/** Returns the first-touch UTM snapshot for this session.
 *  Self-healing: if no snapshot exists but the live URL still has utm_*,
 *  snapshot it now and return it. Empty object when no utm anywhere. */
export function getUtm(): Record<string, string> {
  if (typeof window === "undefined") return {};
  return snapshotLandingUtmOnce() ?? {};
}

function getReferrerHost(): string | null {
  if (typeof document === "undefined") return null;
  const ref = document.referrer;
  if (!ref) return null;
  try {
    const u = new URL(ref);
    if (u.host === window.location.host) return null; // internal nav
    return u.host.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function isAdminPath(p?: string): boolean {
  const path = p ?? (typeof window !== "undefined" ? window.location.pathname : "");
  return typeof path === "string" && path.startsWith("/admin");
}

export async function track(
  type: "pageview" | "pageleave" | "custom" | "button_click" | "scroll_depth" | "rage_click" = "pageview",
  metadata: Record<string, unknown> = {},
  path?: string,
) {
  // Never track admin UI interactions — they pollute site analytics.
  if (isAdminPath(path)) return;
  // Respect per-browser opt-out (deepc team exclusion).
  if (isOptedOut()) return;
  // Gate analytics behind Cookiebot statistics consent.
  if (!hasStatisticsConsent()) {
    queueUntilConsent(() => void track(type, metadata, path));
    return;
  }
  try {
    const visitor_id = getVisitorId();
    const session_id = getSessionId();
    const body = JSON.stringify({
      visitor_id,
      session_id,
      type,
      path: path ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
      referrer_host: getReferrerHost(),
      utm: getUtm(),
      ua: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      metadata,
    });

    // Use fetch with keepalive for ALL events (incl. on-unload pageleave/scroll_depth).
    // sendBeacon was silently dropped here: cross-origin POST application/json triggers
    // a CORS preflight that sendBeacon can't satisfy. keepalive survives unload AND
    // lets us send the apikey as a header (the proven, CORS-allowed path).
    await fetch(`${FN_BASE}/crm-track`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
      body,
      keepalive: true,
    });
  } catch {
    /* swallow */
  }
}

// --- Pending-event queue, flushed when the user accepts statistics ---------
const pending: Array<() => void> = [];
let consentSubscribed = false;
function queueUntilConsent(fn: () => void) {
  pending.push(fn);
  if (consentSubscribed) return;
  consentSubscribed = true;
  onConsentChange((s) => {
    if (!s.statistics) {
      pending.length = 0;
      return;
    }
    const drain = pending.splice(0, pending.length);
    drain.forEach((f) => {
      try {
        f();
      } catch {
        /* ignore */
      }
    });
  });
}

export async function submitLead(payload: {
  source: string;
  email: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  job_title?: string;
  consent?: boolean;
  form_data?: Record<string, unknown>;
}) {
  // Lead submission is an explicit user action, so it is always allowed.
  // Only attach the visitor id when we already have statistics consent.
  const visitor_id = hasStatisticsConsent() ? getVisitorId() : undefined;
  const res = await fetch(`${FN_BASE}/crm-submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
    body: JSON.stringify({
      ...payload,
      visitor_id,
      page_url: typeof window !== "undefined" ? window.location.href : undefined,
      referrer: typeof document !== "undefined" ? document.referrer : undefined,
      utm: getUtm(),
    }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error ? JSON.stringify(j.error) : "Submission failed");
  }
  return res.json() as Promise<{ ok: true; contact_id: string; account_matched: boolean }>;
}

void supabase;

// --- Idempotency guards ----------------------------------------------------
// Module-level init runs at import. HMR / React Refresh can re-execute the
// module, which would re-add document/window listeners AND re-wrap
// history.pushState (each wrap chains the previous wrapped version → N×
// growth of flush() per SPA navigation, manifesting as duplicated
// scroll_depth / pageleave / click events). Guard every init behind a
// window-scoped flag so re-imports are no-ops.
type TrackerWindow = Window & {
  __dcTrackInit?: { click?: boolean; rage?: boolean; scroll?: boolean };
};
function trackerFlags() {
  const w = window as TrackerWindow;
  if (!w.__dcTrackInit) w.__dcTrackInit = {};
  return w.__dcTrackInit;
}

// --- Click tracking --------------------------------------------------------
function initClickTracking() {
  if (typeof document === "undefined") return;
  if (isOptedOut()) return;
  const flags = trackerFlags();
  if (flags.click) return;
  flags.click = true;
  document.addEventListener(
    "click",
    (e) => {
      if (isAdminPath()) return;
      const target = e.target as Element | null;
      if (!target) return;
      const el = target.closest<HTMLElement>(
        "button, a, [role='button'], [data-track]",
      );
      if (!el) return;
      const text = (el.innerText || el.textContent || "").trim().slice(0, 120);
      const metadata: Record<string, unknown> = {
        element_text: text || undefined,
        element_id: el.id || undefined,
        element_tag: el.tagName.toLowerCase(),
        data_track: el.dataset.track || undefined,
        href: (el as HTMLAnchorElement).href || undefined,
        path: window.location.pathname,
        x: (e as MouseEvent).clientX,
        y: (e as MouseEvent).clientY,
      };
      void track("button_click", metadata);
    },
    { capture: true, passive: true },
  );
}
initClickTracking();

// --- Rage-click tracking ---------------------------------------------------
function initRageClickTracking() {
  if (typeof document === "undefined") return;
  if (isOptedOut()) return;
  const flags = trackerFlags();
  if (flags.rage) return;
  flags.rage = true;
  const RAGE_WINDOW_MS = 1000;
  const RAGE_RADIUS_PX = 40;
  const RAGE_THRESHOLD = 3;
  let buffer: { x: number; y: number; t: number }[] = [];

  document.addEventListener(
    "click",
    (e) => {
      if (isAdminPath()) return;
      const me = e as MouseEvent;
      const now = Date.now();
      buffer = buffer.filter((b) => now - b.t <= RAGE_WINDOW_MS);
      buffer.push({ x: me.clientX, y: me.clientY, t: now });

      const nearby = buffer.filter(
        (b) => Math.hypot(b.x - me.clientX, b.y - me.clientY) <= RAGE_RADIUS_PX,
      );
      if (nearby.length < RAGE_THRESHOLD) return;

      const el = (e.target as HTMLElement | null) ?? null;
      const text = ((el?.innerText || el?.textContent) ?? "").trim().slice(0, 120);
      const anchor = (el?.closest?.("a") as HTMLAnchorElement | null) ?? null;

      void track("rage_click", {
        element_text: text || undefined,
        element_id: el?.id || undefined,
        element_tag: el?.tagName?.toLowerCase() || undefined,
        data_track: el?.dataset?.track || undefined,
        href: anchor?.href || undefined,
        path: window.location.pathname,
        click_count: nearby.length,
        x: me.clientX,
        y: me.clientY,
      });

      buffer = []; // one event per burst
    },
    { capture: true, passive: true },
  );
}
initRageClickTracking();

// --- Scroll-depth tracking -------------------------------------------------
function initScrollDepthTracking() {
  if (typeof window === "undefined") return;
  if (isOptedOut()) return;
  const flags = trackerFlags();
  if (flags.scroll) return;
  flags.scroll = true;

  const MILESTONES = [25, 50, 75, 100] as const;
  const DEBUG =
    new URLSearchParams(window.location.search).get("debug") === "scroll";
  let reached = new Set<number>();
  let currentPath = window.location.pathname;
  let rafScheduled = false;

  const measure = () => {
    rafScheduled = false;
    if (isAdminPath()) return;
    const doc = document.documentElement;
    const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
    const raw = ((window.scrollY || 0) / scrollable) * 100;
    const pct = raw >= 99 ? 100 : Math.floor(raw);
    if (DEBUG) console.debug("[scroll_depth] measure pct=", pct);
    for (const m of MILESTONES) if (pct >= m) reached.add(m);
  };

  const onScroll = () => {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(measure);
  };

  const flush = () => {
    if (reached.size === 0) return;
    const ms = Array.from(reached).sort((a, b) => a - b);
    reached.clear();
    if (DEBUG) console.debug("[scroll_depth] flush", ms, currentPath);
    for (const milestone of ms) {
      void track("scroll_depth", { milestone, path: currentPath }, currentPath);
    }
  };

  const reset = () => {
    flush();
    currentPath = window.location.pathname;
    reached = new Set();
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("pagehide", flush);

  let lastPath = currentPath;
  const checkPath = () => {
    if (window.location.pathname !== lastPath) {
      reset();
      lastPath = window.location.pathname;
    }
  };
  window.addEventListener("popstate", checkPath);
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...args: Parameters<typeof origPush>) {
    const r = origPush.apply(this, args);
    checkPath();
    return r;
  };
  history.replaceState = function (...args: Parameters<typeof origReplace>) {
    const r = origReplace.apply(this, args);
    checkPath();
    return r;
  };

  measure();
}
initScrollDepthTracking();
